import React, { useState, useEffect } from 'react';
import './index.scss';
import { readConsent, writeConsent, applyConsent, defaultPreferences } from '../../consentManager';

const SECTIONS = [
  {
    key: 'essential',
    title: 'I. Essential Site Functions',
    alwaysOn: true,
    description:
      'These tools are strictly necessary for the ALL3D platform to function. They allow you to upload models, use our AI tools, and stay logged in.',
    impact: 'Privacy Impact: No personal data is used for tracking; these are functional only.',
  },
  {
    key: 'analytics',
    title: 'II. Product Analytics & Metadata',
    alwaysOn: false,
    description:
      'We use PostHog and Google Analytics to understand how our features are used. This involves recording "routing data" (metadata like your IP address and click path) to help our developers fix bugs and optimize the 3D rendering engine.',
    impact: null,
  },
  {
    key: 'advertising',
    title: 'III. Advertising & Marketing',
    alwaysOn: false,
    description:
      'We use the Meta Pixel and Google Tags to measure the success of our ads. These tools help us show you relevant ALL3D updates on other platforms and prevent you from seeing the same ad twice.',
    impact: 'Privacy Impact: This involves sharing your interaction data with social media and search platforms.',
  },
  {
    key: 'sales',
    title: 'IV. Sales Intelligence & Prospecting',
    alwaysOn: false,
    description:
      'We use Samplead to identify business interests and professional profiles of our visitors. This involves the recording of your IP address and browsing history to help our sales team provide relevant 3D solutions.',
    impact: 'Privacy Impact: This data is used to identify you for outbound sales communication.',
  },
];

const Toggle = ({ checked, onChange }) => (
  <label className="ccb-toggle">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="ccb-toggle__slider" />
  </label>
);

const CustomizeModal = ({ prefs, onChange, onSave, onClose }) => (
  <div className="ccb-modal" onClick={onClose}>
    <div className="ccb-modal__panel" onClick={(e) => e.stopPropagation()}>
      <div className="ccb-modal__header">
        <h2 className="ccb-modal__title">Customize Privacy Settings</h2>
        <button className="ccb-modal__close" onClick={onClose} type="button" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="ccb-modal__sections">
        {SECTIONS.map((section) => (
          <div key={section.key} className="ccb-modal__section">
            <div className="ccb-modal__section-header">
              <h3 className="ccb-modal__section-title">{section.title}</h3>
              {section.alwaysOn ? (
                <span className="ccb-modal__always-on">Always On</span>
              ) : (
                <Toggle
                  checked={prefs[section.key] || false}
                  onChange={(val) => onChange({ ...prefs, [section.key]: val })}
                />
              )}
            </div>
            <p className="ccb-modal__section-desc">{section.description}</p>
            {section.impact && (
              <p className="ccb-modal__section-impact">{section.impact}</p>
            )}
          </div>
        ))}
      </div>

      <div className="ccb-modal__footer">
        <button className="ccb-btn ccb-btn--ghost" onClick={onClose} type="button">
          Cancel
        </button>
        <button className="ccb-btn ccb-btn--primary" onClick={onSave} type="button">
          Save Preferences
        </button>
      </div>
    </div>
  </div>
);

function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [customPrefs, setCustomPrefs] = useState(defaultPreferences);

  useEffect(() => {
    if (!readConsent()) {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const all = { analytics: true, advertising: true, sales: true };
    writeConsent(all);
    applyConsent(all);
    setVisible(false);
  };

  const handleRejectAll = () => {
    writeConsent(defaultPreferences);
    applyConsent(defaultPreferences);
    setVisible(false);
  };

  const handleOpenCustomize = () => {
    setCustomPrefs(defaultPreferences);
    setShowCustomize(true);
  };

  const handleSavePrefs = () => {
    writeConsent(customPrefs);
    applyConsent(customPrefs);
    setShowCustomize(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div className="ccb">
        <div className="ccb__content">
          <h3 className="ccb__heading">Your Privacy Matters</h3>
          <p className="ccb__text">
            ALL3D uses third-party tools (Meta, Google, PostHog, and Samplead) to record site
            interactions and routing data, including your IP address, for analytics and sales
            prospecting. Click &apos;Accept All&apos; to consent to this recording, or &apos;Reject
            All&apos; to disable non-essential tracking.
          </p>
        </div>
        <div className="ccb__actions">
          <button className="ccb-btn ccb-btn--secondary" onClick={handleAcceptAll} type="button">
            Accept All
          </button>
          <button className="ccb-btn ccb-btn--secondary" onClick={handleRejectAll} type="button">
            Reject All
          </button>
          <button className="ccb-btn ccb-btn--ghost" onClick={handleOpenCustomize} type="button">
            Customize
          </button>
        </div>
      </div>

      {showCustomize && (
        <CustomizeModal
          prefs={customPrefs}
          onChange={setCustomPrefs}
          onSave={handleSavePrefs}
          onClose={() => setShowCustomize(false)}
        />
      )}
    </>
  );
}

export default CookieConsentBanner;
